import type {
  IntegrationDescription,
  Job,
  TriggerDescription,
  ValueData,
} from "@smartsuite/automation-engine-api";
import { JobStatus_Stage } from "@smartsuite/automation-engine-api";
import { observer } from "mobx-react-lite";
import React, { type ReactElement } from "react";
import { useEffect, useState } from "react";
import { translateTrigger } from "../../../../../locales/i18n";
import { useRootStore } from "../../../../../stores/hooks";
import {
  isSmartSuiteIntegration,
  TriggerLabel,
} from "../../../../../utils/constants";
import { ActivityDetailsGroup } from "../ActivityDetailsGroup/ActivityDetailsGroup";
import type { ActivityGroupStatus } from "../ActivityDetailsGroup/ActivityDetailsGroup";
import { ActivityDetailsItem } from "../ActivityDetailsItem/ActivityDetailsItem";
import { ActivityDetailsTriggerGroup } from "../ActivityDetailsTriggerGroup/ActivityDetailsTriggerGroup";
import { triggerProps } from "./trigger-props";
import { useTriggerProps } from "./props-hooks";
import { valueDataToStringsWithDelimenters } from "@smartsuite/automation-conditions";
import {
  addEmptyValueIfNeeded,
  getErrorText,
} from "../../../ActivityGrid/ActivityGrid.helpers";
import { ActionError } from "../ActionError/ActionError";
import { t } from "i18next";
import { useWebhookTriggerProps } from "./webhook-trigger-item.hook";

interface TriggerResultGroupProps {
  job: Job;
  triggerTime: string;
}

export const TriggerResultGroup = observer<TriggerResultGroupProps>(
  ({ job, triggerTime }) => {
    const { trigger } = job;
    const { integrations } = useRootStore();

    const propsHook = useTriggerProps(trigger);
    const webhookPropsHook = useWebhookTriggerProps(trigger);
    const [selectedIntegration, setSelectedIntegration] = useState<
      IntegrationDescription | undefined
    >(() =>
      integrations.list.find(
        (i) => i.integrationId === trigger?.triggerReference?.integrationId,
      ),
    );
    const [selectedTrigger, setSelectedTrigger] = useState<
      TriggerDescription | undefined
    >(() =>
      selectedIntegration?.triggers.find(
        (t) => t.triggerId === trigger?.triggerReference?.triggerId,
      ),
    );

    useEffect(() => {
      setSelectedIntegration(
        integrations.list.find(
          (i) => i.integrationId === trigger?.triggerReference?.integrationId,
        ),
      );
    }, [integrations, trigger]);

    useEffect(() => {
      setSelectedTrigger(
        selectedIntegration?.triggers.find(
          (t) => t.triggerId === trigger?.triggerReference?.triggerId,
        ),
      );
    }, [selectedIntegration, trigger]);
    if (trigger === undefined) {
      return null;
    }
    const getTriggerLabel = (): string => {
      if (selectedTrigger === undefined || selectedIntegration === undefined) {
        return "";
      }
      return `${selectedIntegration.label}: ${translateTrigger(
        selectedTrigger,
      )}`;
    };

    const props = (prop: string): ReactElement => {
      switch (prop) {
        case "solutionId":
          return (
            <ActivityDetailsItem label={t("job-list.solution-id")}>
              {propsHook.getSolutionId()}
            </ActivityDetailsItem>
          );
        case "solutionName":
          return (
            <ActivityDetailsItem label={t("job-list.solution-name")}>
              {propsHook.getSolutionName()}
            </ActivityDetailsItem>
          );
        case "applicationId":
          return (
            <ActivityDetailsItem label={t("job-list.app-id")}>
              {propsHook.getApplicationId()}
            </ActivityDetailsItem>
          );
        case "applicationName":
          return (
            <ActivityDetailsItem label={t("job-list.app-name")}>
              {propsHook.getApplicationName()}
            </ActivityDetailsItem>
          );
        case "recordId":
          return (
            <ActivityDetailsItem label={t("job-list.record-url")}>
              <a
                target={"_blank"}
                href={propsHook.getRecordUrl()}
                rel="noopener"
              >
                {propsHook.getRecordId()}
              </a>
            </ActivityDetailsItem>
          );
        case "recordTitle":
          return (
            <ActivityDetailsItem label={t("job-list.record-title")}>
              {propsHook.getTitle()}
            </ActivityDetailsItem>
          );
        case "executionTimestamp":
          return (
            <ActivityDetailsItem label={t("job-list.execution-timestamp")}>
              {triggerTime}
            </ActivityDetailsItem>
          );
        default:
          return <></>;
      }
    };

    const buildTriggerProps = (triggerId: string): React.ReactNode => {
      if (triggerId === TriggerLabel.webhook) {
        return webhookPropsHook.props();
      }

      const propsToRender = triggerProps[triggerId] ?? [];

      return propsToRender.map((prop) => (
        <React.Fragment key={prop}>{props(prop)}</React.Fragment>
      ));
    };

    const getOutputValue = (value: ValueData | undefined): string => {
      return valueDataToStringsWithDelimenters(value).join("; ");
    };

    const truncateIfNeeded = (text: string): string => {
      if (text.length <= 150) {
        return text;
      }
      return `${text.substring(0, 150)}...`;
    };

    const isTriggerFailed = job.status?.stage === JobStatus_Stage.TRIGGER_FAIL;
    const isConditionsNotMet =
      job.status?.stage === JobStatus_Stage.CONDITIONS_NOT_MET;
    const isRejected = job.status?.stage === JobStatus_Stage.TRIGGER_REJECTED;
    // A run that never executed still has the incoming payload to show, which
    // renderErrorInputs is what renders.
    const hasTriggerError = isTriggerFailed || isConditionsNotMet || isRejected;
    const shouldShowError = isTriggerFailed;

    type TriggerOutput = NonNullable<typeof job.trigger>["outputs"][number];

    const renderRecordUrlOutput = (output: TriggerOutput): ReactElement => {
      if (
        output.outputId !== "context-output-record-url" ||
        !Array.isArray(output.value?.values) ||
        output.value?.values.length === 0 ||
        output.value?.values[0].value.oneofKind !== "string"
      ) {
        return <></>;
      }

      const urlString = output.value?.values[0].value.string;
      const parts = urlString.split("=");
      const recordId = parts[parts.length - 1];

      return (
        <ActivityDetailsItem label={t("job-list.record-url")}>
          <a target="_blank" href={urlString} rel="noopener">
            {recordId}
          </a>
        </ActivityDetailsItem>
      );
    };

    const renderTriggerOutput = (output: TriggerOutput): ReactElement => {
      if (output.outputId === "change-event-s3-file") {
        // skip this output as we need it to retry trigger only
        return <></>;
      }

      const recordUrlOutput = renderRecordUrlOutput(output);
      if (recordUrlOutput.type !== React.Fragment) {
        return recordUrlOutput;
      }

      return (
        <ActivityDetailsItem label={output.outputLabel}>
          {addEmptyValueIfNeeded(
            truncateIfNeeded(getOutputValue(output.value)),
          )}
        </ActivityDetailsItem>
      );
    };

    const renderSuccessInputs = (): ReactElement | null => {
      if (hasTriggerError || selectedIntegration === undefined) {
        return null;
      }

      if (isSmartSuiteIntegration(selectedIntegration.integrationId)) {
        return (
          <ActivityDetailsTriggerGroup title="Input">
            {buildTriggerProps(selectedTrigger?.triggerId ?? "")}
          </ActivityDetailsTriggerGroup>
        );
      }

      return (
        <ActivityDetailsTriggerGroup title="Input">
          {job.trigger?.outputs.map((output) => (
            <ActivityDetailsItem
              key={output.outputId}
              label={output.outputLabel}
            >
              {addEmptyValueIfNeeded(
                truncateIfNeeded(getOutputValue(output.value)),
              )}
            </ActivityDetailsItem>
          ))}
        </ActivityDetailsTriggerGroup>
      );
    };

    const renderErrorInputs = (): ReactElement | null => {
      if (!hasTriggerError) {
        return null;
      }

      return (
        <>
          <ActivityDetailsTriggerGroup title="Input">
            {job.trigger?.outputs.map((output) => (
              <React.Fragment key={output.outputId}>
                {renderTriggerOutput(output)}
              </React.Fragment>
            ))}
          </ActivityDetailsTriggerGroup>
          {shouldShowError && (
            <ActionError errorMessage={getErrorText(job.status?.error)} />
          )}
          {isRejected && (
            <ActionError errorMessage={job.status?.message ?? ""} />
          )}
        </>
      );
    };

    const triggerGroupStatus = (): ActivityGroupStatus => {
      if (isTriggerFailed) {
        return "is_error";
      }
      if (isRejected) {
        return "rejected";
      }
      return "is_success";
    };

    return (
      <ActivityDetailsGroup
        label={getTriggerLabel()}
        status={triggerGroupStatus()}
      >
        {renderSuccessInputs()}
        {renderErrorInputs()}
      </ActivityDetailsGroup>
    );
  },
);
